import { useCallback, useMemo } from 'react';
import { MusicVideoService } from '../service/music-video/MusicVideoService';
import { getMusicVideoService } from '../service/ServiceRegistry';
import type {
  MusicVideo,
  MusicVideosQueryParams,
  CreateMusicVideoRequest,
  UpdateMusicVideoRequest,
} from '../types/api/music-video';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';

export function useMusicVideos() {
  const service = useMemo(() => new MusicVideoService(), []);

  const fetchMusicVideos: FetchDataFn<MusicVideo> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<MusicVideo>> => {
      const query: MusicVideosQueryParams = {
        page: params.page,
        size: params.pageSize,
        keyword: params.search,
      };
      const response = await service.getMusicVideos(query);
      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [service]
  );

  return { fetchMusicVideos };
}

export function useMusicVideoActions() {
  const service = getMusicVideoService();

  const createMusicVideo = useCallback(
    async (
      data: CreateMusicVideoRequest,
      thumbnailFile?: File,
      videoFile?: File
    ): Promise<MusicVideo> => {
      return service.createMusicVideo(data, thumbnailFile, videoFile);
    },
    [service]
  );

  const updateMusicVideo = useCallback(
    async (
      musicVideoId: string,
      data: UpdateMusicVideoRequest,
      thumbnailFile?: File,
      videoFile?: File
    ): Promise<MusicVideo> => {
      return service.updateMusicVideo(musicVideoId, data, thumbnailFile, videoFile);
    },
    [service]
  );

  const getMusicVideo = useCallback(
    async (musicVideoId: string): Promise<MusicVideo> => {
      return service.getMusicVideo(musicVideoId);
    },
    [service]
  );

  const deleteMusicVideo = useCallback(
    async (musicVideoId: string): Promise<void> => {
      return service.deleteMusicVideo(musicVideoId);
    },
    [service]
  );

  return { createMusicVideo, updateMusicVideo, getMusicVideo, deleteMusicVideo };
}
